import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "@/lib/auth";
import { sanitizeRichText } from "@/lib/sanitize";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatPrice, fmtEventDateTime } from "@/lib/format";
import { ChevronLeft, Mail, Trash2, Edit } from "lucide-react";
import ComplimentaryCodesPanel from "@/components/ComplimentaryCodesPanel";

export default function AdminEventBookingsPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api(`/events/${id}`),
        api(`/events/${id}/bookings`),
      ]);
      const evD = r1.ok ? await r1.json() : { event: null };
      const bkD = r2.ok ? await r2.json() : { bookings: [] };
      setData({ event: evD.event, bookings: bkD.bookings || [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const emailCsv = async () => {
    setBusy(true);
    try {
      const r = await api(`/events/${id}/email-bookings-csv`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || "Email failed");
      toast.success(`CSV emailed (${d.row_count} rows).`);
    } finally { setBusy(false); }
  };

  const cancelBooking = async (bid) => {
    if (!window.confirm("Cancel this booking (admin override)?")) return;
    const r = await api(`/bookings/${bid}/cancel`, { method: "POST", body: JSON.stringify({ mode: "cancel" }) });
    if (r.ok) { toast.success("Booking cancelled"); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Cancel failed"); }
  };

  const deleteEvent = async () => {
    if (!window.confirm("Delete this event and cascade cancel every booking? This cannot be undone.")) return;
    const r = await api(`/events/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Event deleted"); nav("/admin"); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Delete failed"); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!data?.event) return <div className="text-sm text-destructive">Event not found</div>;

  const e = data.event;

  return (
    <div>
      <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={16} /> Back to Admin
      </button>
      <PageHeader
        title={e.name}
        subtitle={fmtEventDateTime(e.starts_at)}
        actions={
          <div className="flex gap-2">
            <Button onClick={() => nav(`/admin/events/${id}/edit`)} variant="outline" data-testid="ev-edit"><Edit size={14} className="mr-1"/> Edit</Button>
            <Button onClick={emailCsv} disabled={busy} data-testid="ev-email-csv"><Mail size={14} className="mr-1" /> Email CSV</Button>
            <Button variant="destructive" onClick={deleteEvent} data-testid="ev-delete"><Trash2 size={14} className="mr-1" /> Delete event</Button>
          </div>
        }
      />
      <Card className="p-5 mb-4">
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline">{e.stripe_account_label}</Badge>
          <Badge variant="outline">{formatPrice(e.price_pence, e.currency)}</Badge>
          <Badge variant="outline">Cap. {e.capacity}</Badge>
          <Badge variant="outline">Taken {e.places_taken}</Badge>
          {typeof e.places_remaining === "number" && <Badge variant="outline">{e.places_remaining} left</Badge>}
        </div>
        {e.description && <div className="prose prose-sm max-w-none tiptap" dangerouslySetInnerHTML={{ __html: sanitizeRichText(e.description) }} />}
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">Bookings ({data.bookings.length})</h3>
        {data.bookings.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No bookings yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Booker</th>
                <th className="py-2 pr-3">Places</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Booked</th>
                <th className="py-2 pr-3"></th>
              </tr></thead>
              <tbody>
                {data.bookings.map((b) => {
                  const cancelled = (b.status || "").startsWith("cancelled");
                  return (
                    <tr key={b.booking_id} className="border-b border-border/60">
                      <td className="py-2 pr-3">
                        <div>{b.user_name}</div>
                        <div className="text-xs text-muted-foreground">{b.user_email}</div>
                      </td>
                      <td className="py-2 pr-3">{b.places}</td>
                      <td className="py-2 pr-3">{formatPrice(b.amount_pence, b.currency)}</td>
                      <td className="py-2 pr-3">{b.status}</td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString("en-GB")}</td>
                      <td className="py-2 pr-3">
                        {!cancelled && (
                          <Button size="sm" variant="ghost" onClick={() => cancelBooking(b.booking_id)} data-testid={`cancel-${b.booking_id}`}>Cancel</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {e.complimentary_codes_enabled && (
        <ComplimentaryCodesPanel eventId={id} mode="admin" />
      )}
    </div>
  );
}
